import axios from "axios";
import {
  PublicClientApplication,
  AuthenticationResult,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from "@azure/msal-browser";
import { loginRequest } from "../auth-config";

let msalInstance: PublicClientApplication | null = null;
let isRedirectInProgress = false;

const axiosInstance = axios.create({
  timeout: 30000,
});

const ensureMsalInstance = (): PublicClientApplication => {
  if (!msalInstance) {
    throw new Error("MSAL instance not set!");
  }
  return msalInstance;
};

export class AuthenticationRequiredError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = "AuthenticationRequiredError";
  }
}

const getAccessToken = async (): Promise<string> => {
  const msal = ensureMsalInstance();
  const activeAccount = msal.getActiveAccount();

  if (!activeAccount) {
    throw new AuthenticationRequiredError("No active account");
  }

  try {
    const authResult: AuthenticationResult = await msal.acquireTokenSilent({
      ...loginRequest,
      account: activeAccount,
    });

    return authResult.accessToken;
  } catch (error) {
    console.error("Silent token acquisition failed:", error);

    // Only attempt redirect for specific errors that require user interaction
    // and only if a redirect isn't already in progress
    if (
      (error instanceof InteractionRequiredAuthError ||
        error instanceof BrowserAuthError) &&
      !isRedirectInProgress
    ) {
      isRedirectInProgress = true;

      msal.acquireTokenRedirect(loginRequest).catch(() => {
        isRedirectInProgress = false;
      });
    }

    throw new AuthenticationRequiredError(
      "Token acquisition failed",
      error
    );
  }
};

export const setMsalInstance = (instance: PublicClientApplication): void => {
  msalInstance = instance;
  isRedirectInProgress = false;
  setupInterceptors();
};

const setupInterceptors = (): void => {
  // Add request interceptor for token refresh
  axiosInstance.interceptors.request.use(
    async (request) => {
      try {
        const token = await getAccessToken();
        request.headers.authorizationToken = `Bearer ${token}`;
      } catch (error) {
        console.error("Error acquiring token:", error);
      }
      return request;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 403) {
        console.warn("403 Forbidden error caught in interceptor.");
        window.location.href = "/error403";
        return Promise.reject(new Error("Redirecting to /error403"));
      }

      if (error.response) {
        console.error(
          `API call failed with status code: ${error.response.status}`,
        );
        console.error("Response data:", error.response.data);
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Error in API call:", error.message);
      }

      return Promise.reject(error);
    },
  );
};

// Export the single shared axios instance
export const spire360ApiClient = axiosInstance;
